import React, { useCallback, useEffect, useState } from 'react';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import {
  AssignmentLimitUiModel,
  AssignmentRuleUiModel
} from '../../../shared/ui-models/team.ui-model';
import {
  getAssignmentLimits,
  getAssignmentRules,
  upsertAssignmentLimit
} from '../../../shared/services/data/team.data';
import { birdToast } from '../../../shared/components/core-ui/toast';
import { LoadingData } from '../../../ui/LoadingData';
import { NotFoundData } from '../../../ui/NotFoundData';
import {
  describeLoadError,
  LoadError
} from '../../../shared/components/core-ui/load-error/load-error.component';
import { TEAM_ROLES } from '../team-structure/components/member-form-modal.component';
import { CHIP } from '../../../LeadModule/shared/lead-visuals';

const LABEL = 'mb-1 block text-sm font-gilroy-medium text-neutral-400';
const TH = 'whitespace-nowrap px-3 py-3 font-gilroy-bold text-neutral-500';
const TD = 'border-t border-gray-100 px-3 py-3';

export const AssignmentSettingsContainer: React.FC = () => {
  const [limits, setLimits] = useState<AssignmentLimitUiModel[]>([]);
  const [rules, setRules] = useState<AssignmentRuleUiModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>();
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const load = useCallback(() => {
    setLoading(true);
    return Promise.all([getAssignmentLimits(), getAssignmentRules()]).then(
      ([limitsResult, rulesResult]) => {
        setLoadError(isErrorInfo(limitsResult) ? describeLoadError(limitsResult) : undefined);
        if (!isErrorInfo(limitsResult)) setLimits(limitsResult);
        if (!isErrorInfo(rulesResult)) setRules(rulesResult);
        setLoading(false);
      }
    );
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const limitFor = (role: string): AssignmentLimitUiModel | undefined =>
    limits.find((limit: AssignmentLimitUiModel) => limit.role === role);

  const handleSaveLimit = (role: string) => {
    const raw = (drafts[role] ?? '').trim();
    // Empty means unlimited, which the service stores as null. Zero is a real
    // and different setting: it takes the role out of rotation entirely.
    const value = raw.length === 0 ? null : Number(raw);
    if (value !== null && (!Number.isInteger(value) || value < 0)) {
      birdToast.error('A cap must be a whole number, or blank for unlimited');
      return;
    }

    const promise = upsertAssignmentLimit(role, value).then((response: boolean | ErrorInfo) => {
      if (isErrorInfo(response)) throw response;
      return load();
    });

    birdToast.promise(promise, {
      loadingMessage: 'Saving cap...',
      successMessage: 'Cap saved',
      errorPermissionMessage: 'You do not have permission to change assignment caps',
      errorFailedMessage: 'Failed to save cap'
    });
  };

  return (
    <div className="flex h-full flex-col gap-5 md:min-h-[calc(100vh-45px)] md:bg-[#f5f7fc] md:px-[2rem] md:py-[3rem]">
      <section>
        <span
          className="mr-3 inline-block h-8 w-1.5 align-middle rounded-full bg-primary-500"
          aria-hidden="true"
        />
        <h1 className="inline-block align-middle font-gilroy-bold text-2xl text-neutral-500">
          Assignment
        </h1>
      </section>

      {loading ? (
        <LoadingData />
      ) : loadError ? (
        <LoadError message={loadError} onRetry={load} />
      ) : (
        <>
          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-md">
            <h2 className="font-gilroy-bold text-base text-neutral-500">
              Active-lead cap per role
            </h2>
            <p className="mb-4 text-xs text-neutral-400">
              How many open leads one person of this role may hold. When everyone in a matched
              rule&apos;s pool is at their cap the lead is left <strong>unassigned</strong> rather
              than forced onto someone — it stays visible in the queue to be picked up. Blank means
              unlimited; 0 takes the role out of rotation.
            </p>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {TEAM_ROLES.map((role: { value: string; label: string }) => {
                const limit = limitFor(role.value);
                const draft =
                  drafts[role.value] ??
                  (limit && limit.maxActiveLeads !== null ? String(limit.maxActiveLeads) : '');
                return (
                  <div key={role.value}>
                    <label className={LABEL}>{role.label}</label>
                    <div className="flex items-center gap-2">
                      <input
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={draft}
                        placeholder="unlimited"
                        onChange={(event) =>
                          setDrafts({ ...drafts, [role.value]: event.target.value })
                        }
                      />
                      <button
                        type="button"
                        className="whitespace-nowrap text-sm text-primary-500"
                        onClick={() => handleSaveLimit(role.value)}
                      >
                        Save
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-neutral-300">
                      {!limit || limit.isUnlimited
                        ? 'currently unlimited'
                        : `currently ${limit.maxActiveLeads}`}
                    </p>
                  </div>
                );
              })}
            </div>
          </section>

          <section>
            <h2 className="mb-1 font-gilroy-bold text-base text-neutral-500">Routing rules</h2>
            <p className="mb-3 text-xs text-neutral-400">
              The most specific matching rule wins — rules are tried by number of criteria,
              descending, so a catch-all is only reached when nothing narrower fits. A matched rule
              is the only one used: assignment does <strong>not</strong> fall through to a broader
              rule when its pool is full.
            </p>

            {rules.length === 0 ? (
              <NotFoundData />
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-md">
                <table className="w-full min-w-[820px] text-left text-sm">
                  <thead>
                    <tr className="bg-creams-200">
                      {['Rule', 'Matches on', 'Assignees', 'Flags'].map((column: string) => (
                        <th key={column} className={TH}>
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rules.map((rule: AssignmentRuleUiModel) => (
                      <tr key={rule.id} className="hover:bg-creams-100">
                        <td className={`${TD} font-gilroy-medium text-neutral-500`}>{rule.name}</td>
                        <td className={TD}>
                          {rule.isCatchAll ? (
                            <span className={`${CHIP} bg-secondary-50 text-secondary-700`}>
                              Catch-all
                            </span>
                          ) : (
                            <span className="flex flex-wrap gap-1">
                              {Object.keys(rule.criteria).map((key: string) => (
                                <span
                                  key={key}
                                  className={`${CHIP} bg-primary-50 text-primary-700`}
                                >
                                  {key}: {String(rule.criteria[key])}
                                </span>
                              ))}
                            </span>
                          )}
                        </td>
                        <td className={`${TD} text-neutral-400`}>
                          {rule.assigneeNames.length ? (
                            rule.assigneeNames.join(', ')
                          ) : (
                            <span className="text-secondary-600">
                              no assignees — this rule can never assign
                            </span>
                          )}
                        </td>
                        <td className={`${TD} whitespace-nowrap`}>
                          {rule.skipCap ? (
                            <span className={`${CHIP} bg-secondary-100 text-secondary-800`}>
                              Ignores caps
                            </span>
                          ) : null}
                          {!rule.isActive ? (
                            <span className={`ml-1 ${CHIP} bg-neutral-50 text-neutral-300`}>
                              Inactive
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};
